import { resolveLayerEntries } from "./map-layer-state.js";
import { normalizeBridgePointSamples } from "./map-bridge-output.js";
import {
  zoneDisplayNameFromCatalog,
} from "./map-zone-catalog.js";
import { mapText } from "./map-i18n.js";
import { itemIconLookup, resolveFishItemIconUrl } from "./map-item-icons.js";

const DEFAULT_FACT_ICON = "information-circle";

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function trimString(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

function factLabelFallback(value) {
  return trimString(value).replace(/_/g, " ") || mapText("hover.details");
}

function normalizeRgbTriplet(input) {
  if (Array.isArray(input) && input.length >= 3) {
    const [r, g, b] = input.map((value) => Number.parseInt(value, 10));
    return [r, g, b].every((value) => Number.isInteger(value) && value >= 0 && value <= 255)
      ? [r, g, b]
      : null;
  }
  if (isPlainObject(input)) {
    const r = Number.parseInt(input.r, 10);
    const g = Number.parseInt(input.g, 10);
    const b = Number.parseInt(input.b, 10);
    return [r, g, b].every((value) => Number.isInteger(value) && value >= 0 && value <= 255)
      ? [r, g, b]
      : null;
  }
  return null;
}

function rgbDisplayValue(rgb) {
  return Array.isArray(rgb) ? rgb.join(",") : "";
}

function rgbSwatchValue(rgb) {
  return Array.isArray(rgb) ? `${rgb[0]} ${rgb[1]} ${rgb[2]}` : "";
}

function rgbTripletFromU32(value) {
  const rgb = Number.parseInt(value, 10);
  if (!Number.isInteger(rgb) || rgb < 0) {
    return null;
  }
  return [(rgb >> 16) & 0xff, (rgb >> 8) & 0xff, rgb & 0xff];
}

function formatWorldCoordinateComponent(value) {
  if (value == null || value === "") {
    return "";
  }
  const coordinate = Number(value);
  if (!Number.isFinite(coordinate)) {
    return "";
  }
  const formatted = coordinate.toFixed(0);
  return formatted === "-0" ? "0" : formatted;
}

function formatWorldCoordinateXz(source) {
  const worldX = formatWorldCoordinateComponent(source?.worldX);
  const worldZ = formatWorldCoordinateComponent(source?.worldZ);
  return worldX && worldZ ? `${worldX},${worldZ}` : "";
}

function formatWorldCoordinate(source) {
  const worldX = formatWorldCoordinateComponent(source?.worldX);
  const worldY = formatWorldCoordinateComponent(source?.worldY);
  const worldZ = formatWorldCoordinateComponent(source?.worldZ);
  return worldX && worldY && worldZ ? `${worldX},${worldY},${worldZ}` : "";
}

function formatSampleTimestamp(tsUtc) {
  const seconds = Number.parseInt(tsUtc, 10);
  if (!Number.isInteger(seconds)) {
    return "";
  }
  const date = new Date(seconds * 1000);
  if (!Number.isFinite(date.getTime())) {
    return "";
  }
  return `${date.toISOString().slice(0, 19).replace("T", " ")} UTC`;
}

function fishCatalogLookup(stateBundle) {
  const fish = Array.isArray(stateBundle?.state?.catalog?.fish)
    ? stateBundle.state.catalog.fish
    : [];
  return new Map(
    fish
      .map((entry) => [Number.parseInt(entry?.fishId, 10), entry])
      .filter(([fishId]) => Number.isInteger(fishId)),
  );
}

function zoneSummary(zoneRgb, zoneCatalog) {
  const rgb = rgbTripletFromU32(zoneRgb);
  const name =
    trimString(zoneDisplayNameFromCatalog(zoneCatalog, zoneRgb)) ||
    mapText("search.zone.fallback", { zone: `#${Number(zoneRgb).toString(16).padStart(6, "0")}` });
  return {
    zoneRgb,
    name,
    ...(rgb ? { swatchRgb: rgbSwatchValue(rgb) } : {}),
  };
}

function normalizePointSamples(samples) {
  const normalized = normalizeBridgePointSamples(samples).map((sample) => ({
    ...sample,
    sampleId: sample.sampleId ?? null,
  }));
  const fishCounts = new Map();
  for (const sample of normalized) {
    fishCounts.set(sample.fishId, (fishCounts.get(sample.fishId) || 0) + sample.sampleCount);
  }
  return normalized.sort((left, right) =>
    (fishCounts.get(right.fishId) || 0) - (fishCounts.get(left.fishId) || 0) ||
    right.sampleCount - left.sampleCount ||
    right.lastTsUtc - left.lastTsUtc ||
    left.fishId - right.fishId ||
    (left.sampleId || 0) - (right.sampleId || 0),
  );
}

function currentZoneRgbFromSource(source) {
  const zoneSample = (Array.isArray(source?.layerSamples) ? source.layerSamples : []).find(
    (sample) => trimString(sample?.layerId) === "zone_mask",
  );
  const zoneRgb = Number.parseInt(zoneSample?.rgbU32, 10);
  return Number.isInteger(zoneRgb) && zoneRgb >= 0 ? zoneRgb : null;
}

function visibleSampleZones(zones, currentZoneRgb) {
  if (!Number.isInteger(currentZoneRgb)) {
    return zones;
  }
  if (zones.length === 1 && zones[0]?.zoneRgb === currentZoneRgb) {
    return [];
  }
  return zones;
}

export function buildPointSampleRows({ source = null, stateBundle = null, zoneCatalog = [] } = {}) {
  const fishById = fishCatalogLookup(stateBundle);
  const itemById = itemIconLookup(stateBundle?.state?.catalog?.items);
  const currentZoneRgb = currentZoneRgbFromSource(source);
  return normalizePointSamples(source?.pointSamples).map((sample, index) => {
    const fish = fishById.get(sample.fishId) || {};
    const itemId = Number.parseInt(fish?.itemId, 10);
    const fullZoneSet = new Set(sample.fullZoneRgbs);
    const zoneKind =
      sample.fullZoneRgbs.length && sample.zoneRgbs.every((zoneRgb) => fullZoneSet.has(zoneRgb))
        ? "full"
        : "partial";
    const zoneRgbs = zoneKind === "full" ? sample.fullZoneRgbs : sample.zoneRgbs;
    const zones = zoneRgbs
      .map((zoneRgb) => ({
        ...zoneSummary(zoneRgb, zoneCatalog),
        zoneKind: fullZoneSet.has(zoneRgb) ? "full" : "partial",
      }));
    return {
      kind: "point-sample",
      key: `point-sample:${sample.fishId}:${sample.sampleCount}:${sample.sampleId || ""}:${sample.lastTsUtc}:${sample.zoneRgbs.join(",")}:${sample.fullZoneRgbs.join(",")}:${index}`,
      fishId: sample.fishId,
      sampleId: sample.sampleId,
      itemId: Number.isInteger(itemId) ? itemId : null,
      fishName: trimString(fish?.name) || mapText("info.fish.unknown"),
      grade: trimString(fish?.grade),
      isPrize: fish?.isPrize === true,
      iconUrl: Number.isInteger(itemId)
        ? resolveFishItemIconUrl(itemId, {
            iconItemId: fish?.iconItemId,
            iconImage: fish?.iconImage,
            itemLookup: itemById,
          })
        : "",
      sampleCount: sample.sampleCount,
      timestampText: formatSampleTimestamp(sample.lastTsUtc),
      zoneKind,
      zones: visibleSampleZones(zones, currentZoneRgb),
    };
  });
}

function normalizeDetailFacts(sample) {
  if (!Array.isArray(sample?.detailSections)) {
    return [];
  }
  const rows = [];
  for (const section of sample.detailSections) {
    if (!isPlainObject(section) || trimString(section.kind) !== "facts") {
      continue;
    }
    const sectionTitle = trimString(section.title);
    for (const fact of Array.isArray(section.facts) ? section.facts : []) {
      if (!isPlainObject(fact)) {
        continue;
      }
      const key = trimString(fact.key);
      const label = trimString(fact.label) || sectionTitle || factLabelFallback(key);
      const value = trimString(fact.value);
      if (!key || !label || !value) {
        continue;
      }
      rows.push({
        key,
        label,
        value,
        icon: trimString(fact.icon),
        statusIcon: trimString(fact.statusIcon),
        statusIconTone: trimString(fact.statusIconTone),
      });
    }
  }
  return rows;
}

function detailFactByKeys(sample, factKeys) {
  const wanted = new Set((Array.isArray(factKeys) ? factKeys : []).map(trimString).filter(Boolean));
  if (!wanted.size) {
    return null;
  }
  return normalizeDetailFacts(sample).find((fact) => wanted.has(fact.key)) || null;
}

function definitionsForLayer(layerId) {
  const definitions = {
    minimap: [
      {
        key: "height",
        factKeys: ["height"],
        name: "Height",
        label: "Height",
        icon: "mountain-2-fill",
        defaultVisible: false,
      },
      {
        key: "world_coordinate_xz",
        factKeys: [],
        name: mapText("hover.world_coordinate_xz"),
        label: mapText("hover.world_coordinate_xz"),
        icon: "crosshair",
        defaultVisible: false,
      },
      {
        key: "world_coordinate",
        factKeys: [],
        name: mapText("hover.world_coordinate"),
        label: mapText("hover.world_coordinate"),
        icon: "crosshair",
        defaultVisible: false,
      },
    ],
    zone_mask: [
      {
        key: "zone",
        factKeys: ["zone"],
        name: mapText("hover.zone_name"),
        label: mapText("hover.zone"),
        icon: "hover-zone",
        defaultVisible: true,
      },
      {
        key: "rgb",
        factKeys: ["rgb"],
        name: mapText("hover.rgb"),
        label: mapText("hover.rgb"),
        defaultVisible: true,
      },
      {
        key: "extents_confidence",
        factKeys: ["extents_confidence"],
        name: mapText("hover.extents_confidence"),
        label: mapText("hover.extents_confidence"),
        icon: "information-circle",
        defaultVisible: false,
      },
      {
        key: "lineage",
        factKeys: ["lineage"],
        name: mapText("hover.lineage"),
        label: mapText("hover.lineage"),
        icon: "clock",
        defaultVisible: false,
      },
    ],
    region_groups: [
      {
        key: "resource_group",
        factKeys: ["resource_group", "resources", "resource_region"],
        name: mapText("hover.resources"),
        label: mapText("hover.resources"),
        icon: "hover-resources",
        defaultVisible: false,
      },
    ],
    regions: [
      {
        key: "origin_region",
        factKeys: ["origin_region"],
        name: mapText("hover.origin"),
        label: mapText("hover.origin"),
        icon: "trade-origin",
        defaultVisible: false,
      },
      {
        key: "affiliated_node",
        factKeys: ["affiliated_node"],
        name: mapText("hover.affiliated_node"),
        label: mapText("hover.affiliated_node"),
        icon: "map-pin",
        defaultVisible: false,
      },
    ],
  };
  return definitions[trimString(layerId)] || [];
}

function zoneMaskFactRow(definition, sample, zoneCatalog) {
  if (definition.key === "zone") {
    const zoneRgb = Number.isFinite(Number(sample?.rgbU32)) ? Number(sample.rgbU32) : null;
    const zoneName =
      zoneRgb != null ? trimString(zoneDisplayNameFromCatalog(zoneCatalog, zoneRgb)) : "";
    const detailFact = detailFactByKeys(sample, definition.factKeys);
    const value = zoneName || detailFact?.value || "";
    return {
      key: definition.key,
      name: definition.name,
      label: definition.label,
      value,
      icon: definition.icon,
      defaultVisible: definition.defaultVisible,
      ...(detailFact?.statusIcon ? { statusIcon: detailFact.statusIcon } : {}),
      ...(detailFact?.statusIconTone ? { statusIconTone: detailFact.statusIconTone } : {}),
    };
  }

  if (definition.key === "rgb") {
    const rgb = normalizeRgbTriplet(sample?.rgb);
    return {
      key: definition.key,
      name: definition.name,
      label: definition.label,
      value: rgbDisplayValue(rgb),
      defaultVisible: definition.defaultVisible,
      ...(rgb ? { swatchRgb: rgbSwatchValue(rgb) } : {}),
    };
  }

  if (definition.key === "extents_confidence" || definition.key === "lineage") {
    const detailFact = detailFactByKeys(sample, definition.factKeys);
    let value = detailFact?.value || "";
    if (definition.key === "extents_confidence" && (value === "known" || value === "guess")) {
      value = mapText(`hover.confidence.${value}`);
    }
    return {
      key: definition.key,
      name: definition.name,
      label: definition.label,
      value,
      icon: definition.icon,
      defaultVisible: definition.defaultVisible,
    };
  }

  return null;
}

function knownFactRowsForSample(layerId, sample, zoneCatalog, source) {
  return definitionsForLayer(layerId)
    .map((definition) => {
      if (trimString(layerId) === "zone_mask") {
        return zoneMaskFactRow(definition, sample, zoneCatalog);
      }
      if (trimString(layerId) === "minimap" && definition.key === "world_coordinate_xz") {
        return {
          key: definition.key,
          name: definition.name,
          label: definition.label,
          value: formatWorldCoordinateXz(source),
          icon: definition.icon,
          defaultVisible: definition.defaultVisible,
        };
      }
      if (trimString(layerId) === "minimap" && definition.key === "world_coordinate") {
        return {
          key: definition.key,
          name: definition.name,
          label: definition.label,
          value: formatWorldCoordinate(source),
          icon: definition.icon,
          defaultVisible: definition.defaultVisible,
        };
      }
      const fact = detailFactByKeys(sample, definition.factKeys);
      return {
        key: definition.key,
        name: definition.name,
        label: definition.label,
        value: fact?.value || "",
        icon: definition.icon || fact?.icon || DEFAULT_FACT_ICON,
        defaultVisible: definition.defaultVisible,
        ...(fact?.statusIcon ? { statusIcon: fact.statusIcon } : {}),
        ...(fact?.statusIconTone ? { statusIconTone: fact.statusIconTone } : {}),
      };
    })
    .filter(Boolean);
}

function discoveredFactRowsForSample(layerId, sample) {
  const consumedKeys = new Set(
    definitionsForLayer(layerId)
      .flatMap((definition) => definition.factKeys)
      .map(trimString)
      .filter(Boolean),
  );
  return normalizeDetailFacts(sample)
    .filter((fact) => !consumedKeys.has(fact.key))
    .map((fact) => ({
      key: fact.key,
      name: fact.label || factLabelFallback(fact.key),
      label: fact.label || factLabelFallback(fact.key),
      value: fact.value,
      icon: fact.icon || DEFAULT_FACT_ICON,
      defaultVisible: false,
      ...(fact.statusIcon ? { statusIcon: fact.statusIcon } : {}),
      ...(fact.statusIconTone ? { statusIconTone: fact.statusIconTone } : {}),
    }));
}

function visibilityOverride(visibilityByLayer, layerId, factKey) {
  const layerState = isPlainObject(visibilityByLayer?.[layerId]) ? visibilityByLayer[layerId] : null;
  return layerState && typeof layerState[factKey] === "boolean" ? layerState[factKey] : null;
}

export function normalizeHoverFactVisibilityByLayer(raw) {
  if (!isPlainObject(raw)) {
    return {};
  }
  const next = {};
  for (const [layerIdInput, facts] of Object.entries(raw)) {
    const layerId = trimString(layerIdInput);
    if (!layerId || !isPlainObject(facts)) {
      continue;
    }
    const nextFacts = {};
    for (const [factKeyInput, enabled] of Object.entries(facts)) {
      const factKey = trimString(factKeyInput);
      if (!factKey || typeof enabled !== "boolean") {
        continue;
      }
      nextFacts[factKey] = enabled;
    }
    if (Object.keys(nextFacts).length) {
      next[layerId] = nextFacts;
    }
  }
  return next;
}

export function buildLayerHoverSettingsRows({
  layerId,
  sample = null,
  source = null,
  zoneCatalog = [],
  visibilityByLayer = {},
} = {}) {
  const normalizedLayerId = trimString(layerId);
  const rows = [
    ...knownFactRowsForSample(normalizedLayerId, sample, zoneCatalog, source),
    ...discoveredFactRowsForSample(normalizedLayerId, sample),
  ];
  const seen = new Set();
  return rows.flatMap((row) => {
    const key = trimString(row?.key);
    if (!key || seen.has(key)) {
      return [];
    }
    seen.add(key);
    const defaultVisible = row.defaultVisible === true;
    const override = visibilityOverride(visibilityByLayer, normalizedLayerId, key);
    return [
      {
        ...row,
        key,
        enabled: override == null ? defaultVisible : override,
      },
    ];
  });
}

function hoverFactRowsForLayer(layerId, sample, source, visibilityByLayer, zoneCatalog) {
  return buildLayerHoverSettingsRows({
    layerId,
    sample,
    source,
    zoneCatalog,
    visibilityByLayer,
  }).filter((row) => row.enabled && trimString(row.value));
}

function orderedHoverLayerIds(hover, stateBundle) {
  const layerSamples = Array.isArray(hover?.layerSamples) ? hover.layerSamples : [];
  const sampleByLayerId = new Map(
    layerSamples
      .map((sample) => [trimString(sample?.layerId), sample])
      .filter(([layerId]) => layerId),
  );
  const orderedLayerIds = resolveLayerEntries(stateBundle || {})
    .map((layer) => trimString(layer?.layerId))
    .filter((layerId) => sampleByLayerId.has(layerId))
    .reverse();
  return orderedLayerIds.length
    ? orderedLayerIds
    : layerSamples.map((sample) => trimString(sample?.layerId)).filter(Boolean);
}

export function patchTouchesLayerHoverFactSignals(patch) {
  if (!isPlainObject(patch)) {
    return false;
  }
  return Boolean(
    patch._map_runtime?.catalog?.layers != null ||
      patch._map_ui?.layers?.hoverFactsVisibleByLayer != null ||
      patch._map_bridged?.filters?.layerIdsOrdered != null,
  );
}

export function buildLayerHoverFactRows({
  hover = null,
  stateBundle = null,
  visibilityByLayer = {},
  zoneCatalog = [],
} = {}) {
  const layerSamples = Array.isArray(hover?.layerSamples) ? hover.layerSamples : [];
  if (!layerSamples.length) {
    return [];
  }
  const sampleByLayerId = new Map(
    layerSamples
      .map((sample) => [trimString(sample?.layerId), sample])
      .filter(([layerId]) => layerId),
  );
  const layerRows = orderedHoverLayerIds(hover, stateBundle).flatMap((layerId) => {
    const sample = sampleByLayerId.get(layerId);
    if (!sample) {
      return [];
    }
    return hoverFactRowsForLayer(layerId, sample, hover, visibilityByLayer, zoneCatalog).map(
      (row) => ({
        layerId,
        key: row.key,
        icon: row.icon || DEFAULT_FACT_ICON,
        label: row.label || row.name,
        value: row.value,
        ...(row.swatchRgb ? { swatchRgb: row.swatchRgb } : {}),
        ...(row.statusIcon ? { statusIcon: row.statusIcon } : {}),
        ...(row.statusIconTone ? { statusIconTone: row.statusIconTone } : {}),
      }),
    );
  });
  return layerRows;
}

function previewSourceForLayer(layerId, sources = []) {
  const normalizedLayerId = trimString(layerId);
  if (!normalizedLayerId) {
    return { source: null, sample: null };
  }
  for (const source of sources) {
    const sample = (Array.isArray(source?.layerSamples) ? source.layerSamples : []).find(
      (candidate) => trimString(candidate?.layerId) === normalizedLayerId,
    );
    if (sample) {
      return { source, sample };
    }
  }
  return { source: null, sample: null };
}

export function buildLayerPanelHoverFactPreview({
  layerId,
  hover = null,
  selection = null,
  zoneCatalog = [],
  visibilityByLayer = {},
} = {}) {
  const { source, sample } = previewSourceForLayer(layerId, [hover, selection]);
  return buildLayerHoverSettingsRows({
    layerId,
    sample,
    source,
    zoneCatalog,
    visibilityByLayer,
  });
}

export function nextHoverFactVisibilityByLayer(
  currentVisibilityByLayer,
  layerIdInput,
  factKeyInput,
  enabled,
) {
  const layerId = trimString(layerIdInput);
  const factKey = trimString(factKeyInput);
  if (!layerId || !factKey) {
    return normalizeHoverFactVisibilityByLayer(currentVisibilityByLayer);
  }
  const next = normalizeHoverFactVisibilityByLayer(cloneJson(currentVisibilityByLayer || {}));
  const layerState = isPlainObject(next[layerId]) ? next[layerId] : {};
  layerState[factKey] = enabled === true;
  next[layerId] = layerState;
  return next;
}
